/**
 * SSOAccessPanel — Continue-with-Google / Microsoft / Apple / Organization
 * block for the Login page. Sits ABOVE the email/password fields, with an
 * "Or continue with email" divider underneath.
 *
 * Today:
 *   • Google      → live via Emergent-managed OAuth (no setup needed)
 *   • Microsoft   → "Coming soon" toast (needs Azure AD app reg)
 *   • Apple       → "Coming soon" toast (needs Apple Developer account)
 *   • Organization → opens an email modal that captures the work email and
 *                    routes to the IT contact (SSO playbook to be wired
 *                    when the first enterprise customer provides their
 *                    SAML metadata).
 */
import { useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SSOAccessPanel({ lang = "en" }) {
  const [busy, setBusy] = useState("");

  const startGoogle = () => {
    // Emergent OAuth — redirect URL must be dynamic (no fallback string).
    const origin = window.location.origin;
    const dest = `${origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(dest)}`;
  };

  const comingSoon = (provider) => {
    toast.info(
      lang === "pt"
        ? `${provider} estará disponível em breve. Use Google ou e-mail.`
        : lang === "es"
        ? `${provider} estará disponible pronto. Usa Google o correo.`
        : `${provider} sign-in is coming soon. Use Google or email for now.`
    );
  };

  const startOrg = async () => {
    const email = window.prompt(
      lang === "pt"
        ? "Digite seu e-mail corporativo para verificarmos seu provedor SSO:"
        : "Enter your work email — we'll route you to your org SSO:"
    );
    if (!email || !email.includes("@")) return;
    setBusy("org");
    try {
      await axios.post(`${API}/auth/sso-request`, { email }).catch(() => {});
      toast.success(
        lang === "pt"
          ? "Recebido! Nosso time vai entrar em contato em até 1 dia útil."
          : "Got it. We'll reach out to your IT team within 1 business day."
      );
    } finally {
      setBusy("");
    }
  };

  const orLabel = lang === "pt" ? "ou continue com e-mail"
                 : lang === "es" ? "o continúa con correo"
                 : "or continue with email";

  return (
    <div data-testid="sso-access-panel" className="space-y-2.5 mb-3">
      <button type="button" onClick={startGoogle} data-testid="sso-google"
        className="w-full h-11 rounded-xl border bg-white hover:bg-slate-50 text-[#1F2937] font-semibold text-[13px] inline-flex items-center justify-center gap-3 transition-all"
        style={{ borderColor: "#D1D5DB" }}>
        <GoogleG /> Log In with Google
      </button>

      <button type="button" onClick={() => comingSoon("Microsoft")}
        data-testid="sso-microsoft"
        className="w-full h-11 rounded-xl border bg-white hover:bg-slate-50 text-[#1F2937] font-semibold text-[13px] inline-flex items-center justify-center gap-3 transition-all opacity-90"
        style={{ borderColor: "#D1D5DB" }}>
        <MicrosoftLogo /> Log In with Microsoft
      </button>

      <button type="button" onClick={() => comingSoon("Apple")}
        data-testid="sso-apple"
        className="w-full h-11 rounded-xl border bg-black hover:bg-[#111] text-white font-semibold text-[13px] inline-flex items-center justify-center gap-3 transition-all"
        style={{ borderColor: "#000" }}>
        <AppleLogo /> Sign in with Apple
      </button>

      <button type="button" onClick={startOrg} disabled={busy === "org"}
        data-testid="sso-org"
        className="w-full h-11 rounded-xl border bg-[#1F2937] hover:bg-[#111827] text-white font-semibold text-[13px] inline-flex items-center justify-center gap-3 transition-all"
        style={{ borderColor: "#1F2937" }}>
        {busy === "org"
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <KeyRound className="w-4 h-4" />}
        Log In with your Organization
      </button>

      <div className="relative my-4">
        <div className="absolute inset-0 flex items-center" aria-hidden>
          <div className="w-full border-t" style={{ borderColor: "#E5E7EB" }} />
        </div>
        <div className="relative flex justify-center">
          <span className="px-3 text-[11px] uppercase tracking-wider font-semibold"
                style={{ background: "#FBF9F4", color: "#94A3B8" }}>
            {orLabel}
          </span>
        </div>
      </div>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.51 8.18c0-.62-.05-1.21-.16-1.77H9v3.36h4.21c-.18 1-.74 1.85-1.57 2.42v2.01h2.54c1.49-1.37 2.34-3.39 2.34-6.02z" fill="#4285F4"/>
      <path d="M9 17c2.12 0 3.9-.7 5.19-1.91l-2.54-2.01c-.7.47-1.6.75-2.65.75-2.04 0-3.77-1.38-4.39-3.23H1.97v2.02C3.25 14.85 5.92 17 9 17z" fill="#34A853"/>
      <path d="M4.61 10.6c-.16-.47-.25-.97-.25-1.5s.09-1.03.25-1.5V5.58H1.97C1.4 6.66 1.07 7.79 1.07 9s.33 2.34.9 3.42l2.64-2.02z" fill="#FBBC05"/>
      <path d="M9 4.4c1.15 0 2.18.39 2.99 1.16l2.25-2.25C12.9 1.95 11.12 1.07 9 1.07 5.92 1.07 3.25 3.22 1.97 5.58l2.64 2.02C5.23 5.78 6.96 4.4 9 4.4z" fill="#EA4335"/>
    </svg>
  );
}

function MicrosoftLogo() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
      <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
    </svg>
  );
}

function AppleLogo() {
  return (
    <svg width="16" height="16" viewBox="0 0 384 512" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
    </svg>
  );
}
